import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { IMAGES } from "../lib/data";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5 grid-lines relative">
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-[#050505]" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-orange-500 mb-6 transition-colors" data-testid="back-home-link">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="backdrop-blur-xl bg-[#18181a]/70 border border-white/10 p-9 clip-notch">
          <img src={IMAGES.logo} alt="Sobtrex" className="h-11 w-auto mb-6" />
          <h1 className="font-display text-2xl font-bold uppercase text-white flex items-center gap-2">
            <Lock size={20} className="text-orange-500" /> Admin Login
          </h1>
          <p className="mt-2 text-sm text-zinc-500">Access dealer enquiries &amp; subscribers.</p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <input
              type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
              className="w-full bg-black/50 border border-zinc-800 text-white px-4 py-3 placeholder:text-zinc-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none transition-colors"
            />
            <input
              type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
              className="w-full bg-black/50 border border-zinc-800 text-white px-4 py-3 placeholder:text-zinc-600 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:outline-none transition-colors"
            />
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-[#ff5a00] text-black font-display text-sm font-bold uppercase tracking-wide py-3.5 hover:bg-[#e04f00] transition-colors disabled:opacity-60">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
